import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useQuery } from 'hooks/useCustomQuery';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { axios } from 'services/axios';

import { NORMAL_LOGIN, MFA_LOGIN, MFA_SETUP } from '@/v5/@constants/login';

const useLogin = () => {
    const dispatch = useDispatch();
    const [authResponse, setAuthResponse] = useState(null);
    const [thisAdmin, setThisAdmin] = useState(null);
    const [loginError, setLoginError] = useState({
        isError: false,
        message: '',
    });
    const [isChecked, setIsChecked] = useState(false);
    const [showError, setShowError] = useState(false);

    const adminUserQuery = useQuery({
        queryKey: ['adminUser', authResponse?.token],
        queryFn: async () => {
            if (!authResponse?.token) {
                throw new Error('No token available');
            }

            const response = await fetch(
                `${import.meta.env.VITE_APP_API_URL}/AdminUser/me`,
                {
                    headers: {
                        accept: 'application/json, text/plain, */*',
                        authorization: `bearer ${authResponse.token}`,
                        'X-Requestscope': '20',
                    },
                    method: 'GET',
                    mode: 'cors',
                    credentials: 'omit',
                },
            );
            return response.json();
        },
        enabled: !!authResponse?.token, // Only run when we have a token
        onSuccess: (adminData) => {
            setThisAdmin(adminData);

            // It's an MFA login
            if (adminData.mfaEnabled && !adminData.mfatotpConfirmed) {
                dispatch({
                    type: 'LOGIN_MFA_SUCCESS',
                    payload: {
                        ...authResponse,
                        email: authResponse?.user,
                        showMFA: true,
                        loginAction: MFA_SETUP,
                    },
                });
                toast.info('Please complete your MFA setup.', {
                    autoClose: 3000,
                });
            }
            // It's a normal login
            else {
                dispatch({
                    type: 'LOGIN_SUCCESS',
                    payload: {
                        ...authResponse,
                        ...adminData, // Include apiDocReader
                        email: authResponse?.user,
                        loadAccessList: true,
                    },
                });
            }
        },
        onError: (error) => {
            console.error('Failed to fetch admin user data:', error);
            toast.error('Failed to load user information');
        },
    });

    // Mutation for login authentication
    const loginMutation = useMutation({
        mutationFn: async (credentials) => {
            // NOTE: logout before login to reset state
            dispatch({ type: 'CLEAR_MFA_TOKEN' });
            const response = await axios.post('/auth', {
                username: credentials.email,
                password: credentials.password,
            });
            return response.data;
        },
        onSuccess: (data) => {
            setAuthResponse(data);
            setLoginError({ isError: false, message: '' });

            // No token = MFA login
            if (!data.token) {
                dispatch({
                    type: 'LOGIN_MFA',
                    payload: {
                        loginAction: MFA_LOGIN,
                    },
                });
                toast.info('Please complete your MFA login.', {
                    autoClose: 3000,
                });
                return;
            }

            // Clear previous login state
            dispatch({
                type: 'LOGIN_SUCCESS',
                payload: { id: null },
            });
        },
        onError: (error) => {
            const errorMessage =
                error?.response?.data?.message ||
                'Login failed. Please check your credentials and try again.';
            setLoginError({
                isError: true,
                message: errorMessage,
            });
            toast.error(errorMessage);
        },
    });

    // Main login function
    const onSubmit = async (values) => {
        // Check for BT.com specific validation
        if (isChecked === false && window.location.host === 'uc.bt.com') {
            setShowError(true);
            return;
        }

        loginMutation.mutate(values);
    };

    const handleSubmit = async (values, { setSubmitting, setStatus }) => {
        try {
            setStatus(null);
            await onSubmit(values);

            // Only show success toast if login was successful and no MFA required
            if (!loginMutation.isError && authResponse?.token) {
                toast.success('You have been logged in successfully.');
            }
        } catch (error) {
            const errorMessage =
                error.response?.data?.message ||
                'Failed to log in. Please try again.';

            setStatus(errorMessage);
            toast.error(errorMessage);
        } finally {
            setSubmitting(false);
        }
    };

    // Cancel login function
    const cancelLogin = () => {
        dispatch({ type: 'CLEAR_LOGIN_ACTION' });
    };

    // Reset login state
    const resetLogin = () => {
        dispatch({ type: 'CLEAR_LOGIN_ACTION' });
        setAuthResponse(null);
        setThisAdmin(null);
        setLoginError({ isError: false, message: '' });
        setShowError(false);
    };

    return {
        // State
        authResponse,
        thisAdmin,
        loginError,
        showError,
        isChecked,

        // Loading states
        isLoading: loginMutation.isPending || adminUserQuery.isLoading,
        isLoginLoading: loginMutation.isPending,
        isAdminLoading: adminUserQuery.isLoading,

        // Actions
        onSubmit,
        handleSubmit,
        cancelLogin,
        resetLogin,
        setIsChecked,
        setShowError,

        // Queries/Mutations
        loginMutation,
        adminUserQuery,

        // Constants
        LOGIN_ACTIONS: {
            NORMAL_LOGIN,
            MFA_LOGIN,
            MFA_SETUP,
        },
    };
};

export { useLogin };
